import {notificationCenterService} from "../services/notificationCenterService";
import {frontageApiService} from "@/plugins/frontage-api";
import Vue from "vue";

const state = {
  isNewCounter: 0,
  staffUid: null,
  notifications: [],
  shouldCallLongPolling: true,
  currentPage: 1,
  notificationsLoadCompleted: false
}
const DAYS_IN_MONTH = 30;
const DEFAULT_PER_PAGE = 20;

const getters = {
  getIsNewCounter: state => state.isNewCounter,
  getNotifications: state => state.notifications,
  getUnreadNotifications: state => state.notifications.filter((notification) => !notification.read),
  getNotificationsLoadCompleted: state => state.notificationsLoadCompleted
}

const mutations = {
  setIsNewCounter(state, counter) {
    state.isNewCounter = counter
  },
  setStaffUid(state, staffUid) {
    state.staffUid = staffUid
  },
  setShouldCallLongPolling(state, value) {
    state.shouldCallLongPolling = value
  },
  setNotifications(state, notifications) {
    notifications = notifications || []
    state.notifications = state.notifications.concat(notifications)
  },
  setNotificationStatus(state, {payload}){
    let notificationIdx = state.notifications.findIndex((notification) => notification.uid === payload.notification_uid)
    if (notificationIdx < 0) return
    let notification = state.notifications[notificationIdx]
    Vue.set(notification, 'read', !notification.read)
  },
  setAllRead(state){
    state.notifications = state.notifications.map(notification => ({...notification, read: true}));
  },
  incrementCurrentPage() {
    state.currentPage++
  },
  completeLoading() {
    state.notificationsLoadCompleted = true
  }
}

const actions = {
  async initNotificationCenterBadge({commit, dispatch}, payload){
    try {
      document.addEventListener('visibilitychange', event =>{
        commit('setShouldCallLongPolling', !document.hidden)
      })
      frontageApiService.listen('is-new-notification-counter-reset', () => dispatch('resetNotificationCounter'));
      commit('setStaffUid', payload.staffUid)
      await dispatch('fetchNotificationsCounterLongPolling')
    } catch (e) {
      console.error('error', e);
    }
  },

  async setNotificationReadStatus({commit, state, dispatch}, payload){
    try {
      await notificationCenterService.setNotificationReadStatus(payload.notification_uid, payload.status)
      commit('setNotificationStatus', {payload})
    } catch (e) {
      console.error('error', e)
    }
  },

  async setAllNotificationsAsRead({commit}){
    try {
      await notificationCenterService.setAllNotificationsAsRead();
      commit('setAllRead');
    }
    catch (e){
      console.error('error', e)
    }
  },

  async fetchNotificationsCounterLongPolling({commit, dispatch}){
    notificationCenterService.cancelRequest()

    try {
      if(state.shouldCallLongPolling) {
        let isNewCounter = (await notificationCenterService.getIsNewNotificationCounter(state.isNewCounter, state.staffUid))

        if (isNewCounter === undefined) {
          // add log somehow...
          await new Promise(resolve => setTimeout(resolve, 30000));
          return
        }

        if (isNewCounter !== state.isNewCounter) {
          frontageApiService.send('is-new-notification-counter-update', isNewCounter);
          commit('setIsNewCounter', isNewCounter)
        }
      }
    } catch (e) {
      console.error('error', e);
      await new Promise(resolve => setTimeout(resolve, 30000));
    } finally {
      await new Promise(resolve => setTimeout(resolve, 500));
      await dispatch('notificationsCenterLongPollDebouncer')
    }
  },

  async resetNotificationCounter({commit}){
    try {
      await notificationCenterService.resetNotificationCounter()
      let isNewCounter = 0
      frontageApiService.send('is-new-notification-counter-update', isNewCounter);
      commit('setIsNewCounter', isNewCounter)
    } catch (e) {
      console.error('error', e)
    }
  },

  async fetchNotifications({commit}){
    try {
      let currentType = null
      let typeName = ''
      const allNotifications = await notificationCenterService.getNotifications(DAYS_IN_MONTH, DEFAULT_PER_PAGE, state.currentPage)
      const allMetadata = await notificationCenterService.getMetadataFromLocalStorage(allNotifications)
      const allTypes = await notificationCenterService.getAllTypesFromLocalStorage(allMetadata)

      let mappedNotifications = allNotifications.map(notification => {
        const currentMetadata = allMetadata.find( metadata => metadata.uid === notification.notification_metadata_uid)
        if(currentMetadata){
          if (currentMetadata?.notification_type_uid){
            currentType = allTypes.find( type => type.uid === currentMetadata.notification_type_uid)
            typeName = (currentType && currentType.name) ? currentType.name : ''
          }

          if(currentMetadata.notification_code_name === 'appointment_booking_reminder'){
            const title = notificationCenterService.buildTitleAppointmentBookingReminder(notification)
            const body = notificationCenterService.buildBodyAppointmentBookingReminder(notification)
            notification.text_sent = {
              title: title,
              body: body
            }
          }

          return {...notification, ...{
              notification_type : typeName,
              notification_code_name : currentMetadata.notification_code_name,
              creator_type : currentMetadata.creator_type,
              creator_identifier : currentMetadata.creator_identifier,
              avatarImage: notificationCenterService.getAvatarByType(typeName),
              pane: currentMetadata.pane
            }}
        }
      })
      mappedNotifications = mappedNotifications.filter(notification => notification?.pane)
      if (allNotifications.length < DEFAULT_PER_PAGE) {
        commit('completeLoading')
      }
      commit('setNotifications', mappedNotifications)
      commit('incrementCurrentPage')
    } catch (e) {
      console.error('error', e)
    }
  },

  async notificationsCenterLongPollDebouncer({dispatch}) {
    await new Promise(resolve => setTimeout(resolve, 1000));
    dispatch('fetchNotificationsCounterLongPolling');
  },

  emitCurrentCounter({state}){
    frontageApiService.send('is-new-notification-counter-update', state.isNewCounter);
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
